import { useRouter } from 'next/router'
import { getLink, prefixLocale } from '@helpers/env'
import trackError from '@helpers/trackError'
import { SubmitButton, ButtonLink } from '@components/ui/Button'
import Translations from '@helpers/translations'
import { getReturnLabel } from '@api/returns'
import styles from './styles.module.scss'
import { SubmitButtonHandlers } from '@components/ui/Button/SubmitButton'
import { objectKeys } from '@helpers/utils'
import { type OnError } from '..'

type QRCodeBoxProps = {
  returnID: string
  showQR: boolean
  onError: OnError
  carrierId: number | null
  encodedLabel: string | undefined
  labelEncoding: string | undefined
  labelFormat: string | undefined
}

function QRCodeBox({
  returnID,
  showQR,
  carrierId,
  encodedLabel = '',
  labelEncoding = '',
  labelFormat = '',
  onError
}: QRCodeBoxProps) {
  const router = useRouter()
  const labelLinkSource = `data:${labelEncoding};base64,${encodedLabel}`
  const encodingFormat = labelEncoding.split('/')[1]

  const fetchQRCode = ({
    triggerButtonSuccess,
    triggerButtonError
  }: SubmitButtonHandlers) => {
    const windowReference = window.open('', '_blank') || { location: '' }
    getReturnLabel(returnID, labelFormat)
      .then(res => {
        triggerButtonSuccess()
        windowReference.location = res.data.labelUrl
      })
      .catch(error => {
        triggerButtonError()
        onError({
          type: 'error',
          content: (
            <>
              {Translations.localise(
                'pages.returns.returns-notifications.return-label.copy-1'
              )}{' '}
              <b>
                <a
                  style={{ color: '#DC1E32' }}
                  href={getLink('/pages/contact-us')}
                >
                  {Translations.localise(
                    'pages.returns.returns-notifications.return-label.copy-2'
                  )}
                </a>
              </b>{' '}
              {Translations.localise(
                'pages.returns.returns-notifications.return-label.copy-3'
              )}
            </>
          )
        })
        trackError(new Error('fetchQRCode failed'), { error })
      })
  }

  const completeReturn = () => {
    const queryParams = {
      postcode: router.query.postcode,
      order_number: router.query.order_number,
      return_id: returnID
    }
    const href = `/pages/returns?${objectKeys(queryParams)
      .map(key => `${key}=${queryParams[key]}`)
      .join('&')}`
    const as = `${prefixLocale()}${href}`
    setTimeout(() => router.push(href, as, { shallow: true }), 300) // wait for transition to finish
  }

  return (
    <div className={styles.returnsQrCode}>
      <div className={styles.returnsQrCode__container}>
        <span className={styles.returnsQrCode__title}>
          {carrierId
            ? Translations.localise('pages.returns.label-generated')
            : Translations.localise('pages.returns.last-step')}
        </span>
        <span className={styles.returnsQrCode__text}>
          {carrierId
            ? `${
                showQR
                  ? Translations.localise('pages.returns.qr-code-message')
                  : Translations.localise('pages.returns.label-message')
              } ${Translations.localise('pages.returns.view-download')}`
            : Translations.localise('pages.returns.select-returns-courier')}
        </span>
        {carrierId && encodedLabel && (
          <ButtonLink
            href={labelLinkSource}
            download={`return-${returnID}.${encodingFormat}`}
          >
            {showQR
              ? Translations.localise('pages.returns.view-qr-code')
              : Translations.localise('pages.returns.view-returns-label')}
          </ButtonLink>
        )}
        {carrierId && !encodedLabel && (
          <SubmitButton variant="secondary" onClick={fetchQRCode}>
            {showQR
              ? Translations.localise('pages.returns.view-qr-code')
              : Translations.localise('pages.returns.view-returns-label')}
          </SubmitButton>
        )}
        {!carrierId && (
          <SubmitButton variant="secondary" onClick={completeReturn}>
            {Translations.localise('pages.returns.select-courier')}
          </SubmitButton>
        )}
      </div>
    </div>
  )
}

export default QRCodeBox
